import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import StatusChip from "@/components/StatusChip";
import { inr, fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { GraduationCap, ShieldAlert } from "lucide-react";

export default function NeutralDashboard() {
  const [cases, setCases] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/cases"), api.get("/neutrals/me/profile")])
      .then(([cs, pr]) => { setCases(cs.data); setProfile(pr.data); })
      .finally(() => setLoading(false));
  }, []);

  const slaCountdown = (dueIso) => {
    const days = Math.ceil((new Date(dueIso) - new Date()) / 86400000);
    if (isNaN(days)) return "—";
    if (days < 0) return `${Math.abs(days)}d overdue`;
    return `${days}d left`;
  };

  return (
    <div className="space-y-8" data-testid="neutral-dashboard">
      {profile && profile.empanelment_status !== "Active" && (
        <div className="rounded-sm bg-amber-50 border border-amber-200 p-5 flex flex-wrap gap-3 items-center justify-between" data-testid="empanelment-banner">
          <div className="flex gap-3 items-center">
            <ShieldAlert className="h-5 w-5 text-amber-700" />
            <div>
              <div className="font-semibold text-amber-900">Empanelment {profile.empanelment_status}</div>
              <div className="text-sm text-amber-800">Complete the ODR Course to advance to Active. Cases are assigned only after empanelment is Active.</div>
            </div>
          </div>
          <Link to="/app/course"><Button className="rounded-sm bg-primary"><GraduationCap className="h-4 w-4 mr-2" />Go to Course</Button></Link>
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-4">
        <KPI label="Assigned to me" value={cases.length} />
        <KPI label="Active hearings" value={cases.filter(c => c.status === "Hearing Scheduled").length} />
        <KPI label="Cases handled" value={profile?.cases_handled ?? 0} accent />
      </div>

      <div>
        <h3 className="font-display text-2xl text-court mb-4">Assigned queue</h3>
        {loading ? <div className="text-muted-foreground">Loading…</div> : cases.length === 0 ? (
          <div className="rounded-sm border border-dashed border-stone/60 p-12 text-center text-muted-foreground bg-card">
            No cases assigned yet. New assignments will appear here.
          </div>
        ) : (
          <div className="grid gap-3">
            {cases.map((c) => (
              <Link key={c.id} to={`/app/cases/${c.id}`} data-testid={`case-row-${c.case_number}`} className="bg-card rounded-sm border border-stone/60 p-5 flex flex-wrap gap-4 items-center justify-between lift-on-hover">
                <div>
                  <div className="text-xs text-muted-foreground">{c.case_number}</div>
                  <div className="font-semibold text-court mt-0.5">{c.title}</div>
                  <div className="text-xs text-muted-foreground mt-1">Claimant: {c.claimant?.name || "—"} · {c.type}</div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground">SLA</div>
                    <div className="font-semibold text-amber-700">{slaCountdown(c.sla_due_date)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground">Claim</div>
                    <div className="font-semibold">{inr(c.claim_amount)}</div>
                  </div>
                  <StatusChip status={c.status} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function KPI({ label, value, accent }) {
  return (
    <div className={`rounded-sm p-6 border ${accent ? "bg-emerald-50 border-emerald-200" : "bg-card border-stone/60"} `} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="font-display text-4xl font-semibold mt-2 text-court">{value}</div>
    </div>
  );
}
